<script lang="ts">
  import { DEFAULT_ORGANIZATION_TIMEZONE } from "../../../../../../../../_shared/_logic/timezones"
  import { defaultPlanningStartPreset } from "../../../../../../../_shared/_logic/planning-start-preset"
  import AppointmentPlanningSurface from "../../../../../../../_shared/_modules/appointment-planning-surface"
  import { appointmentPlanningDomainOperations } from "../../../../../domain-operations"
  import type {
    AppointmentPlanningExistingPlannedRow,
    AppointmentPlanningExistingSourceRow,
    AppointmentPlanningSubmissionResult,
  } from "../../../../../../../_shared/_logic/appointments-contract"
  import type {
    CancelEventsStepOutput,
    CancelReplanInit,
    CancelReplanMode,
    CancelReplanWorkflowEvent,
    PlanReplacementsStepOutput,
    PlannedRow,
  } from "../../../../_logic/cancel-replan-contract"
  import {
    buildActiveContactOrderByWorkOrderId,
    buildWorkOrderScheduleBatchRows,
  } from "../../../../_logic/rows"
  import { resolveDefaultBatchAppointmentStartDate } from "../../../../../../../_shared/_logic/planning-lead-time"

  let {
    init,
    mode = "createAppointments",
    cancelOutput,
    output,
    readOnly = false,
    onEvent,
    onError,
  }: {
    init: CancelReplanInit
    mode?: CancelReplanMode
    cancelOutput?: CancelEventsStepOutput
    output?: PlanReplacementsStepOutput
    readOnly?: boolean
    onEvent: (event: CancelReplanWorkflowEvent) => void | Promise<void>
    onError: (message: string | null) => void
  } = $props()

  const propertyById = $derived(new Map(init.properties.map((property) => [property.id, property])))
  const workOrderRowById = $derived(new Map((init.sourceRows ?? []).map((row) => [row.id, row])))
  const eventByWorkOrderId = $derived(
    Object.fromEntries(
      init.events
        .filter((event): event is ScheduleEvent & { work_order_id: string } =>
          Boolean(event.work_order_id),
        )
        .map((event) => [event.work_order_id, event]),
    ),
  )
  const activeContactOrderByWorkOrderId = $derived(
    buildActiveContactOrderByWorkOrderId(init.contactOrders ?? []),
  )
  const isRescheduleMode = $derived(mode === "rescheduleAppointments")
  const directRows = $derived(
    buildWorkOrderScheduleBatchRows({
      workOrders: init.workOrders,
      eventByWorkOrderId,
      workOrderRowById,
      propertyById,
      activeContactOrderByWorkOrderId,
    }),
  )
  const rows = $derived(
    (cancelOutput?.rows ?? directRows).filter((row) => !row.removed && row.workOrder),
  )
  const existingRows = $derived<AppointmentPlanningExistingSourceRow[]>(
    rows
      .map((row): AppointmentPlanningExistingSourceRow | null =>
        row.workOrder
          ? ({
              id: row.workOrder.id,
              workOrder: row.workOrder,
              property: row.property,
              previousEvent: row.event,
              defaultPlanningStrategy: isRescheduleMode ? "AUTO" : null,
            } satisfies AppointmentPlanningExistingSourceRow)
          : null,
      )
      .filter((row): row is NonNullable<typeof row> => Boolean(row)),
  )
  const rowByWorkOrderId = $derived(new Map(rows.map((row) => [row.workOrder?.id ?? "", row])))
  const initialExistingPlannedRows = $derived(
    (output?.plannedRows ?? [])
      .filter((row) => row.row.workOrder)
      .map(
        (row): AppointmentPlanningExistingPlannedRow => ({
          id: row.row.workOrder?.id ?? row.id,
          source: {
            id: row.row.workOrder?.id ?? row.id,
            workOrder: row.row.workOrder as WorkOrder,
            property: row.row.property,
            previousEvent: row.previousEvent ?? row.row.event,
            defaultPlanningStrategy: isRescheduleMode ? "AUTO" : null,
          },
          preview: row.preview,
          event: row.event,
        }),
      ),
  )
  const employeeOptions = $derived(
    init.employees
      .map((entry) => ({
        label: entry.label,
        value: String(entry.value ?? entry.id ?? "").trim(),
      }))
      .filter((entry) => entry.value),
  )
  const defaultStartDate = $derived(
    init.startDate ?? resolveDefaultBatchAppointmentStartDate(init.planningSettings),
  )

  const completePlanning = async (submission: AppointmentPlanningSubmissionResult) => {
    if (submission.kind === "committed" && submission.status === "partial") return

    const result = submission.result

    const plannedRows: PlannedRow[] = result.plannedRows
      .map((planned): PlannedRow | null => {
        const sourceRow = rowByWorkOrderId.get(planned.source.workOrder.id)

        if (!sourceRow) return null

        return {
          id: sourceRow.id,
          row: sourceRow,
          preview: planned.preview,
          event: planned.event,
          previousEvent: planned.source.previousEvent ?? sourceRow.event,
        }
      })
      .filter((row): row is PlannedRow => Boolean(row))
    await onEvent({
      type: "planning.committed",
      output: {
        plannedRows,
        contactOrderPreviews: [],
        committed: true,
      },
    })
  }
</script>

<AppointmentPlanningSurface
  organizationId={init.organizationId}
  existingWorkOrderRows={existingRows}
  unitContactContexts={init.unitContactContexts ?? []}
  {initialExistingPlannedRows}
  {employeeOptions}
  workingHoursByUser={init.workingHoursByUser ?? {}}
  flexSlotsPerDay={init.planningSettings?.flex_slots_per_day ?? 0}
  {defaultStartDate}
  defaultStartPreset={defaultPlanningStartPreset()}
  settingsTimezone={init.planningSettings?.timezone ?? DEFAULT_ORGANIZATION_TIMEZONE}
  initialStartDateIsExplicit={Boolean(init.startDate)}
  operations={appointmentPlanningDomainOperations}
  trialRestrictionsActive={init.trialRestrictionsActive}
  existingSubmitLabel={isRescheduleMode ? "Neue Termine planen" : null}
  tableTitle={isRescheduleMode ? "Ersatztermine planen" : null}
  tableDescription={isRescheduleMode
    ? "Planen Sie neue Termine für die stornierten Aufträge."
    : null}
  {readOnly}
  setError={onError}
  onSubmissionResult={completePlanning}
/>
